import React from 'react'
import styled from 'styled-components/macro'

import { USELESS_NFT_ADDRESSES } from '../../constants/addresses'
import { SupportedChainId } from '../../constants/chains'

const PageWrapper = styled.div`
  width: calc(100% - 80px);
  max-width: calc(100vw - 80px);
  overflow: hidden;
  font-size: 20px;
  line-height: 28px;
  font-weight: 200;
  padding: 0 40px;

  code {
    background: #161616;
    padding: 2px 6px;
    color: white;
    font-size: 17px;
    line-height: 24px;
    border-radius: 3px;
    white-space: normal;
  }

  a {
    font-weight: 600 !important;
    color: white !important;
    text-decoration: none !important;
  }

  @media screen and (max-width: 800px) {
    font-size: 16px;

    code {
      font-size: 15px;
      overflow-x: auto;
      white-space: pre-wrap;
      white-space: -moz-pre-wrap;
      white-space: -pre-wrap;
      white-space: -o-pre-wrap;
      word-wrap: break-word;
    }
  }

  @media screen and (max-width: 780px) {
    padding: 0 20px;
    width: calc(100% - 40px);
    max-width: calc(100vw - 40px);
  }

  @media screen and (max-width: 600px) {
    font-size: 14px;
    line-height: 20px;

    code {
      font-size: 12px;
      line-height: 18px;
    }
  }

  @media screen and (max-width: 400px) {
    margin-top: -5vh;
  }
`

const TopSection = styled.div`
  width: 100%;
  text-align: center;
  margin-bottom: 50px;
  margin-top: 2vh;
`

const TitleWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  display: inline-block;
`

const SloganText = styled.div`
  font-weight: 400;
  font-size: 64px;
  margin-bottom: 20px;
  line-height: 75px;

  @media screen and (max-width: 800px) {
    font-size: 58px;
    line-height: 68px;
  }

  @media screen and (max-width: 600px) {
    font-size: 48px;
    line-height: 56px;
  }
`

const SubtitleText = styled.div`
  font-weight: 200;
  font-size: 20px;
  margin-bottom: 15px;
  line-height: 28px;

  @media screen and (max-width: 800px) {
    font-size: 18px;
    line-height: 25px;
  }

  @media screen and (max-width: 600px) {
    font-size: 16px;
    line-height: 20px;
  }
`

const StepsWrapper = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;

  > ol > li {
    margin-bottom: 30px;
  }

  @media screen and (max-width: 600px) {
    margin-left: -20px;
    width: calc(100% + 20px);
  }
`

export default function MyNFTs() {
  const openInNewTab = (url: string) => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
    if (newWindow) {
      newWindow.opener = null
    }
  }

  return (
    <PageWrapper>
      <TopSection>
        <TitleWrapper>
          <SloganText>How to Change NFT Images and Attributes</SloganText>
          <SubtitleText>{`You can change the image and attributes for any Useless NFT tier below yours, so Bronze can change it for Pleb, Silver for Bronze and Pleb, and so on.`}</SubtitleText>
          <SubtitleText>{`Here's a guide on how to do it.`}</SubtitleText>
        </TitleWrapper>
      </TopSection>
      <StepsWrapper>
        <ol>
          <li>
            <p>
              Get acquainted with IPFS. To upload files quickly,{' '}
              <a href={'https://www.pinata.cloud/'} target={'_blank'} rel="noreferrer">
                Pinata
              </a>{' '}
              is an easy place to start.
            </p>
          </li>
          <li>
            <p>
              Generate the JSON files that are used to describe each NFT. The JSON file must adhere to at least the
              following schema:
            </p>
            <code style={{ display: 'block', whiteSpace: 'pre-wrap' }}>
              {`{
  "name": "New Name #100", // use the ID number in the end
  "description": "New Name #100", // use the ID number in the end
  "image": "ipfs://QmTXuAUW4nLqRZ8NCbEaH8tPSYVhqq3hPTYbnqx8p97Yi7", // the IPFS hash of the image, take Pinata's CID column
  "attributes": [
    {
      "trait_type": "Tier",
      "value": "Diamond|Gold|Silver|Bronze|Pleb"
    },
    ... // other optional fields
  ]
}`}
            </code>
            <p>
              Each JSON file should have the name <code>{`/<folder-name>/<id>_<tier>.json`}</code>, where{' '}
              <code>tier</code> is written as follows:
              <ul>
                <li>
                  Diamond: <code>0</code>
                </li>
                <li>
                  Gold: <code>1</code>
                </li>
                <li>
                  Silver: <code>2</code>
                </li>
                <li>
                  Bronze: <code>3</code>
                </li>
                <li>
                  Pleb: <code>4</code>
                </li>
              </ul>
            </p>
            <p>
              As an example, if you wanted to overwrite all <code>{`pleb`}</code> {`NFTs, you'd create:`}
              <ul>
                <li>
                  <code>{`/QmeSjSinHpPnmXmspMjwiXyN6zS4E9zccariGR3jxcaWtq/0_4.json`}</code>
                </li>
                <li>
                  <code>{`/QmeSjSinHpPnmXmspMjwiXyN6zS4E9zccariGR3jxcaWtq/1_4.json`}</code>
                </li>
                <li>...</li>
                <li>
                  <code>{`/QmeSjSinHpPnmXmspMjwiXyN6zS4E9zccariGR3jxcaWtq/9998_4.json`}</code>
                </li>
                <li>
                  <code>{`/QmeSjSinHpPnmXmspMjwiXyN6zS4E9zccariGR3jxcaWtq/9999_4.json`}</code>
                </li>
              </ul>
              Where <code>QmeSjSinHpPnmXmspMjwiXyN6zS4E9zccariGR3jxcaWtq</code> represents the ipfs hash of where your
              10,000 PFP folder was uploaded to IPFS.
            </p>
            <p>
              TIP: To make uploading the images easier, you can create upload a folder with each image so each one
              resolves to, for example:
              <ul>
                <li>
                  <code>QmTXuAUW4nLqRZ8NCbEaH8tPSYVhqq3hPTYbnqx8p97Yi7/1.png</code>
                </li>
                <li>
                  <code>QmTXuAUW4nLqRZ8NCbEaH8tPSYVhqq3hPTYbnqx8p97Yi7/2.png</code>
                </li>
                <li>...</li>
                <li>
                  <code>QmTXuAUW4nLqRZ8NCbEaH8tPSYVhqq3hPTYbnqx8p97Yi7/9998.png</code>
                </li>
                <li>
                  <code>QmTXuAUW4nLqRZ8NCbEaH8tPSYVhqq3hPTYbnqx8p97Yi7/9999.png</code>
                </li>
              </ul>
            </p>
          </li>
          <li>
            Go to the NFT page on{' '}
            <a
              href={`https://etherscan.io/address/${USELESS_NFT_ADDRESSES[SupportedChainId.MAINNET]}#writeContract`}
              target={'_blank'}
              rel="noreferrer"
            >
              Etherscan
            </a>
            {` and connect your wallet.`}
          </li>
          <li>
            <p>
              Go to function number 3, <code>overridePeasantURI</code> and input the following data:
              <ul>
                <li>
                  <code>payableAmount (ether)</code>
                  {` - The amount of ETH you're willing to pay to lock the metadata. Inputting 0 allows your changes to be reverted immediately by another Useless NFT holder.`}
                </li>
                <li>
                  <code>_tokenId</code> - The ID of your Useless NFT.
                </li>
                <li>
                  <code>_tierToOverride</code>
                  {` - The number of the tier you're looking to override. For `}
                  Pleb, input <code>4</code>, Bronze - <code>3</code>, Silver - <code>2</code>, Gold - <code>1</code>,
                  Diamond - <code>0</code>.
                </li>
                <li>
                  <code>_newURI</code> - The IPFS hash of the folder that contains all of the <code>.json</code> files.
                  Using the prior example in number <code>2</code>, the IPFS hash would be{' '}
                  <code>QmeSjSinHpPnmXmspMjwiXyN6zS4E9zccariGR3jxcaWtq</code>.
                </li>
              </ul>
            </p>
          </li>
          <li>
            <p>
              Press the <code>write</code> button and confirm the transaction in your wallet. Once the transaction is
              confirmed on chain, all the NFTs for that tier will have been changed!
            </p>
          </li>
        </ol>
      </StepsWrapper>
    </PageWrapper>
  )
}
